/**
 * Checkbox Group Component
 *
 * Reusable checkbox group component for forms with theme support.
 * Used for selecting multiple options from a list.
 *
 * @module components/ui/CheckboxGroup
 */

import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { BodyText } from '@/components/ui/BodyText';
import { cn } from '@/utils/cn';

/**
 * Props for the CheckboxGroup component
 */
export interface CheckboxGroupProps {
  /** Label for the checkbox group */
  label: string;
  /** Array of option strings to display */
  options: string[];
  /** Array of currently selected values */
  selectedValues: string[];
  /** Callback when a checkbox is toggled */
  onChange: (value: string, checked: boolean) => void;
  /** Whether the field is required */
  required?: boolean;
  /** CSS class for checkbox color theming */
  colorClass?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Validation state */
  state?: 'default' | 'error' | 'warning' | 'success';
  /** Error message to display */
  error?: string;
  /** Warning message to display */
  warning?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display below input */
  helperText?: string;
}

/**
 * CheckboxGroup Component
 *
 * Renders a group of checkboxes with a label, supporting theme switching
 * and customizable styling.
 */
export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  required = false,
  colorClass = 'text-primary-600 focus:ring-primary-500',
  className,
  state = 'default',
  error,
  warning,
  success,
  helperText,
}) => {
  const { isDark } = useTheme();

  // Generate unique IDs for accessibility
  const groupId = `checkbox-group-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${groupId}-error`;
  const warningId = `${groupId}-warning`;
  const successId = `${groupId}-success`;
  const helperId = `${groupId}-helper`;

  // Determine which message to show (priority: error > warning > success > helper)
  const message = error || warning || success || helperText;
  const messageId = error ? errorId : warning ? warningId : success ? successId : helperId;

  // Build aria-describedby
  const describedBy = message ? messageId : undefined;

  // State-based checkbox styling
  const getCheckboxStateClass = () => {
    switch (state) {
      case 'error':
        return 'text-red-600 focus:ring-red-500';
      case 'warning':
        return 'text-yellow-600 focus:ring-yellow-500';
      case 'success':
        return 'text-green-600 focus:ring-green-500';
      default:
        return colorClass;
    }
  };

  return (
    <div className={cn('space-y-1', className)}>
      <label className={cn(
        'block text-sm font-medium mb-3',
        isDark ? 'text-secondary-300' : 'text-secondary-700',
        required && 'after:content-["*"] after:ml-0.5 after:text-red-500'
      )}>
        {label}
      </label>
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
        role="group"
        aria-describedby={describedBy}
        aria-invalid={state === 'error' ? 'true' : 'false'}
        aria-required={required}
      >
        {options.map((option) => (
          <label
            key={option}
            className="flex items-center space-x-2 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={selectedValues.includes(option)}
              onChange={(e) => onChange(option, e.target.checked)}
              className={cn(
                'w-4 h-4 border rounded focus:ring-2',
                getCheckboxStateClass(),
                isDark
                  ? 'border-secondary-600 bg-secondary-800'
                  : 'border-neutral-300 bg-white'
              )}
            />
            <BodyText className={cn(
              'capitalize group-hover:text-primary-600 transition-colors',
              isDark ? "text-secondary-100" : "text-secondary-900"
            )}>
              {option.replace('-', ' ')}
            </BodyText>
          </label>
        ))}
      </div>

      {/* Message */}
      {message && (
        <p
          id={messageId}
          className={cn(
            'text-sm mt-2',
            error && (isDark ? 'text-red-400' : 'text-red-600'),
            warning && (isDark ? 'text-yellow-400' : 'text-yellow-600'),
            success && (isDark ? 'text-green-400' : 'text-green-600'),
            !error && !warning && !success && (isDark ? 'text-secondary-400' : 'text-secondary-600')
          )}
          role={error ? 'alert' : undefined}
          aria-live={error ? 'polite' : undefined}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default CheckboxGroup;